import { useQuery, useLazyQuery, useMutation, useSubscription, gql } from '@apollo/client'
import { useEffect, useContext, useMemo, useRef } from 'react'
import useAdsManagerStore from '@/store/adsManagerStore'
import { showNotification } from '@/utils/notifications'
import { describeBudgetGuardError } from '@/utils/pixel-warmup-panel'
import { campaignBudgetEditFields } from '@/utils/advantage-leads'
import { getCampaignBudgetErrorMessage } from '@/utils/campaign-budget'
import { AuthContext } from '@/context/auth-context'
import {
    createCampaignStatusAction,
    createAdSetStatusAction,
    createAdStatusAction,
    createAdSetBudgetAction,
    addActivityActionToCache,
} from '@/utils/activity-actions'
import {
    GET_ADS_MANAGER_DATA,
    UPDATE_AD_SET_BUDGET,
    TOGGLE_CAMPAIGN_STATUS,
    TOGGLE_AD_SET_STATUS,
    TOGGLE_AD_STATUS,
    DELETE_CAMPAIGN,
    DELETE_ADSET,
    DELETE_AD,
    AD_CAMPAIGN_STATUS_UPDATE,
    UPDATE_AD_CAMPAIGN_ORDER,
    UPDATE_AD_CAMPAIGN,
} from '@/graphql/ads/ad-campaigns-queries'
import { GET_AD_CREATIVES_WITH_ASSETS } from '@/graphql/ad-creatives-library-queries'
import { usePageData } from '@/components/mediaBuying/adCreatives/hooks'

export function useAdsManagerData(
    campaignId,
    skipInitialLoad = false,
    connectedPublishers = null,
    { formatAmount } = {},
) {
    const { dateRange, setCampaigns, setLoading, setError, addProcessingCampaign } = useAdsManagerStore()
    const { user } = useContext(AuthContext)

    // Track if we've completed the initial mount to avoid triggering queries on first render
    const hasInitialMountCompleted = useRef(false)
    const previousDateRange = useRef(dateRange)

    // Use lazy query when we need to skip initial load
    const [executeQuery, { data: lazyData, loading: lazyLoading, error: lazyError }] = useLazyQuery(
        GET_ADS_MANAGER_DATA,
        {
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'cache-first',
        },
    )

    // Creatives of the campaign with their assets and texts, as the My Creatives page loads them, for the ad mockup
    // shown when hovering an ad row. Missing creatives just leave the plain media hover preview.
    const { data: creativesData } = useQuery(GET_AD_CREATIVES_WITH_ASSETS, {
        variables: { campaignId },
        skip: !campaignId,
        errorPolicy: 'all',
    })
    const creativesById = useMemo(
        () =>
            Object.fromEntries(
                (creativesData?.AdCreative?.searchFilterAdCreativesJoinAssets || []).map((creative) => [
                    creative.id,
                    creative,
                ]),
            ),
        [creativesData],
    )

    // Meta page name and icon shown on the ad mockup
    const { pageTitle, pageIcon } = usePageData(connectedPublishers)

    // Transform dateRange to GraphQL input format
    const getDateRangeInput = (dateRange) => {
        if (!dateRange || dateRange === 'all') return null

        // Handle custom date range object
        if (typeof dateRange === 'object' && dateRange.type === 'custom') {
            return {
                custom: {
                    since: dateRange.startDate,
                    until: dateRange.endDate,
                },
            }
        }

        // If dateRange is just 'custom' string without actual dates, return null to prevent query
        if (dateRange === 'custom') {
            return null
        }

        // Map frontend date range values to API preset values
        const presetMap = {
            last_3d: 'last_3d',
            last_7d: 'last_7d',
            last_14d: 'last_14d',
            last_month: 'last_30d',
            last_90d: 'last_90d',
        }

        const preset = presetMap[dateRange]
        if (preset) {
            return { preset }
        }

        return null
    }

    // Regular query for normal cases
    const {
        data: normalData,
        loading: normalLoading,
        error: normalError,
        refetch,
    } = useQuery(GET_ADS_MANAGER_DATA, {
        variables: {
            dateRange: getDateRangeInput(dateRange),
            campaignId: campaignId,
        },
        skip: dateRange === 'custom' || skipInitialLoad, // Skip query when custom is selected or when skipInitialLoad is true
        errorPolicy: 'all',
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-first',
    })

    // Use the appropriate data source based on whether we skipped initial load
    const data = skipInitialLoad ? lazyData : normalData
    const loading = skipInitialLoad ? lazyLoading : normalLoading
    const error = skipInitialLoad ? lazyError : normalError

    // Track initial mount completion
    useEffect(() => {
        // Mark initial mount as completed after first render
        if (!hasInitialMountCompleted.current) {
            hasInitialMountCompleted.current = true
            previousDateRange.current = dateRange
        }
    }, [])

    // Watch for date range changes when using lazy query (but not on initial mount)
    useEffect(() => {
        if (
            skipInitialLoad &&
            executeQuery &&
            hasInitialMountCompleted.current &&
            dateRange !== 'custom' &&
            dateRange !== previousDateRange.current
        ) {
            // Execute lazy query when date range changes (excluding initial mount)
            executeQuery({
                variables: {
                    dateRange: getDateRangeInput(dateRange),
                    campaignId: campaignId,
                },
                fetchPolicy: 'network-only', // Force fresh data for date range changes
            })
            previousDateRange.current = dateRange
        } else if (!skipInitialLoad) {
            // Update previous date range for normal mode too
            previousDateRange.current = dateRange
        }
    }, [dateRange, skipInitialLoad, executeQuery, campaignId])

    // Centralized refetch function
    const performRefetch = async () => {
        try {
            if (skipInitialLoad && executeQuery) {
                // Use lazy query execution with network-only for fresh data
                await executeQuery({
                    variables: {
                        dateRange: getDateRangeInput(dateRange),
                        campaignId: campaignId,
                    },
                    fetchPolicy: 'network-only',
                })
            } else if (refetch) {
                // Use regular refetch (refetch always fetches from network)
                await refetch({
                    dateRange: getDateRangeInput(dateRange),
                    campaignId: campaignId,
                })
            }
        } catch (error) {
            console.error('Error in performRefetch:', error)
        }
    }

    // Subscribe to ad campaign status updates
    useSubscription(AD_CAMPAIGN_STATUS_UPDATE, {
        variables: { userId: user?.id },
        skip: !user?.id,
        onData: ({ data: subscriptionData }) => {
            if (subscriptionData?.data?.adCampaignStatusUpdate) {
                const campaignUpdate = subscriptionData.data.adCampaignStatusUpdate

                if (campaignUpdate.status === 'processing') {
                    // For PROCESSING status: manually add/update campaign in the table
                    addProcessingCampaign({
                        id: campaignUpdate.id,
                        name: campaignUpdate.name,
                        status: 'PROCESSING',
                        error: campaignUpdate.error,
                    })
                } else {
                    // For PUBLISHED/FAILED status: refetch using appropriate method
                    performRefetch()
                }
            }
        },
        onError: (error) => {
            console.error('Ad campaign subscription error:', error)
        },
    })

    // Mutations
    // The daily budget of a campaign with one campaign budget, through the ad campaign update
    const [updateAdCampaignMutation] = useMutation(UPDATE_AD_CAMPAIGN)

    const [updateAdSetBudgetMutation] = useMutation(UPDATE_AD_SET_BUDGET, {
        update(cache, { data }, { variables }) {
            if (data?.updateAdSetBudget) {
                // Update the specific adset budget in cache instead of refetching all data
                const existingData = cache.readQuery({
                    query: GET_ADS_MANAGER_DATA,
                    variables: { dateRange: getDateRangeInput(dateRange), campaignId: campaignId },
                })

                if (existingData) {
                    // Find the adset to get old budget and name for activity action
                    let targetAdSet = null
                    let publisherCurrency = null

                    for (const campaign of existingData.AdCampaign.adsManagerData) {
                        const adSet = campaign.adSets?.find((adSet) => adSet.db_id === variables.adSetId)
                        if (adSet) {
                            targetAdSet = adSet
                            break
                        }
                    }

                    const updatedData = {
                        ...existingData,
                        AdCampaign: {
                            ...existingData.AdCampaign,
                            adsManagerData: existingData.AdCampaign.adsManagerData.map((campaign) => ({
                                ...campaign,
                                adSets: campaign.adSets.map((adSet) =>
                                    adSet.db_id === variables.adSetId
                                        ? { ...adSet, daily_budget: variables.budget, stored_budget: variables.budget }
                                        : adSet,
                                ),
                            })),
                        },
                    }

                    cache.writeQuery({
                        query: GET_ADS_MANAGER_DATA,
                        variables: { dateRange: getDateRangeInput(dateRange), campaignId: campaignId },
                        data: updatedData,
                    })

                    // Generate and add activity action if we found the target adset
                    if (targetAdSet) {
                        try {
                            const adSetIdentifier = cache.identify({
                                __typename: 'AdsManagerAdSet',
                                db_id: variables.adSetId,
                            })

                            const activityAction = createAdSetBudgetAction({
                                adsetName: targetAdSet.name,
                                oldBudget: targetAdSet.daily_budget,
                                newBudget: variables.budget,
                                currency: publisherCurrency || 'AUD', // Default to AUD if not available
                                user,
                            })

                            addActivityActionToCache(cache, adSetIdentifier, activityAction)
                        } catch (error) {
                            console.warn('Failed to add adset budget activity action to cache:', error)
                        }
                    }
                }
            }
        },
    })

    const [toggleCampaignStatusMutation] = useMutation(TOGGLE_CAMPAIGN_STATUS, {
        update(cache, { data }, { variables }) {
            if (data?.toggleCampaignStatus) {
                const campaignIdentifier = cache.identify({
                    __typename: 'AdsManagerCampaign',
                    id: variables.campaignId,
                })

                // Update campaign status
                cache.modify({
                    id: campaignIdentifier,
                    fields: {
                        status() {
                            return variables.currentStatus ? 'PAUSED' : 'ACTIVE'
                        },
                    },
                })

                // Generate and add activity action
                try {
                    const campaignObject = cache.readFragment({
                        id: campaignIdentifier,
                        fragment: gql`
                            fragment CampaignName on AdsManagerCampaign {
                                name
                            }
                        `,
                    })

                    if (campaignObject?.name) {
                        const activityAction = createCampaignStatusAction({
                            campaignName: campaignObject.name,
                            oldStatus: variables.currentStatus,
                            newStatus: !variables.currentStatus,
                            user,
                        })

                        addActivityActionToCache(cache, campaignIdentifier, activityAction)
                    }
                } catch (error) {
                    console.warn('Failed to add campaign status activity action to cache:', error)
                }
            }
        },
        optimisticResponse: ({ campaignId }) => ({
            toggleCampaignStatus: {
                __typename: 'AdsManagerCampaign',
                id: campaignId,
            },
        }),
    })

    const [toggleAdSetStatusMutation] = useMutation(TOGGLE_AD_SET_STATUS, {
        update(cache, { data }, { variables }) {
            if (data?.toggleAdSetStatus) {
                const adSetIdentifier = cache.identify({
                    __typename: 'AdsManagerAdSet',
                    db_id: variables.adSetId,
                })

                // Update adset status
                cache.modify({
                    id: adSetIdentifier,
                    fields: {
                        status() {
                            return variables.currentStatus ? 'PAUSED' : 'ACTIVE'
                        },
                    },
                })

                // Generate and add activity action
                try {
                    const adSetObject = cache.readFragment({
                        id: adSetIdentifier,
                        fragment: gql`
                            fragment AdSetName on AdsManagerAdSet {
                                name
                            }
                        `,
                    })

                    if (adSetObject?.name) {
                        const activityAction = createAdSetStatusAction({
                            adsetName: adSetObject.name,
                            oldStatus: variables.currentStatus,
                            newStatus: !variables.currentStatus,
                            user,
                        })

                        addActivityActionToCache(cache, adSetIdentifier, activityAction)
                    }
                } catch (error) {
                    console.warn('Failed to add adset status activity action to cache:', error)
                }
            }
        },
        optimisticResponse: ({ adSetId }) => ({
            toggleAdSetStatus: {
                __typename: 'AdsManagerAdSet',
                db_id: adSetId,
            },
        }),
    })

    const [toggleAdStatusMutation] = useMutation(TOGGLE_AD_STATUS, {
        update(cache, { data }, { variables }) {
            if (data?.toggleAdStatus) {
                const adIdentifier = cache.identify({
                    __typename: 'AdsManagerAd',
                    id: variables.adId,
                })

                // Update ad status
                cache.modify({
                    id: adIdentifier,
                    fields: {
                        status() {
                            return variables.currentStatus ? 'PAUSED' : 'ACTIVE'
                        },
                    },
                })

                // Generate and add activity action
                try {
                    const adObject = cache.readFragment({
                        id: adIdentifier,
                        fragment: gql`
                            fragment AdName on AdsManagerAd {
                                name
                            }
                        `,
                    })

                    if (adObject?.name) {
                        const activityAction = createAdStatusAction({
                            adName: adObject.name,
                            oldStatus: variables.currentStatus,
                            newStatus: !variables.currentStatus,
                            user,
                        })

                        addActivityActionToCache(cache, adIdentifier, activityAction)
                    }
                } catch (error) {
                    console.warn('Failed to add ad status activity action to cache:', error)
                }
            }
        },
        optimisticResponse: ({ adId }) => ({
            toggleAdStatus: {
                __typename: 'AdsManagerAd',
                id: adId,
            },
        }),
    })

    const [deleteCampaignMutation] = useMutation(DELETE_CAMPAIGN, {
        update(cache, { data }, { variables }) {
            if (data?.deleteCampaign) {
                const existingData = cache.readQuery({
                    query: GET_ADS_MANAGER_DATA,
                    variables: { dateRange: getDateRangeInput(dateRange), campaignId: campaignId },
                })

                if (existingData) {
                    cache.writeQuery({
                        query: GET_ADS_MANAGER_DATA,
                        variables: { dateRange: getDateRangeInput(dateRange), campaignId: campaignId },
                        data: {
                            ...existingData,
                            AdCampaign: {
                                ...existingData.AdCampaign,
                                adsManagerData: existingData.AdCampaign.adsManagerData.filter(
                                    (campaign) => campaign.id !== variables.campaignId,
                                ),
                            },
                        },
                    })
                }
            }
        },
    })

    const [deleteAdSetMutation] = useMutation(DELETE_ADSET, {
        update(cache, { data }, { variables }) {
            if (data?.deleteAdSet) {
                const existingData = cache.readQuery({
                    query: GET_ADS_MANAGER_DATA,
                    variables: { dateRange: getDateRangeInput(dateRange), campaignId: campaignId },
                })

                if (existingData) {
                    cache.writeQuery({
                        query: GET_ADS_MANAGER_DATA,
                        variables: { dateRange: getDateRangeInput(dateRange), campaignId: campaignId },
                        data: {
                            ...existingData,
                            AdCampaign: {
                                ...existingData.AdCampaign,
                                adsManagerData: existingData.AdCampaign.adsManagerData.map((campaign) => ({
                                    ...campaign,
                                    adSets: campaign.adSets.filter((adSet) => adSet.db_id !== variables.adSetId),
                                })),
                            },
                        },
                    })
                }
            }
        },
    })

    const [deleteAdMutation] = useMutation(DELETE_AD, {
        update(cache, { data }, { variables }) {
            if (data?.deleteAd) {
                const existingData = cache.readQuery({
                    query: GET_ADS_MANAGER_DATA,
                    variables: { dateRange: getDateRangeInput(dateRange), campaignId: campaignId },
                })

                if (existingData) {
                    cache.writeQuery({
                        query: GET_ADS_MANAGER_DATA,
                        variables: { dateRange: getDateRangeInput(dateRange), campaignId: campaignId },
                        data: {
                            ...existingData,
                            AdCampaign: {
                                ...existingData.AdCampaign,
                                adsManagerData: existingData.AdCampaign.adsManagerData.map((campaign) => ({
                                    ...campaign,
                                    adSets: campaign.adSets.map((adSet) => ({
                                        ...adSet,
                                        ads: adSet.ads.filter((ad) => ad.db_id !== variables.adId),
                                    })),
                                })),
                            },
                        },
                    })
                }
            }
        },
    })

    const [updateAdCampaignOrderMutation] = useMutation(UPDATE_AD_CAMPAIGN_ORDER)

    // Update store when data changes
    useEffect(() => {
        setLoading(loading)

        if (error) {
            setError(error)
        } else {
            setError(null)
        }

        if (data?.AdCampaign?.adsManagerData) {
            // Server already returns campaigns sorted by their persisted order.
            setCampaigns(data.AdCampaign.adsManagerData)
        }
    }, [data, loading, error, setCampaigns, setLoading, setError])

    // Mutation handlers
    // `expectedBudget` is the saved budget (cents) the page showed: a budget changed meanwhile is not overwritten
    const updateAdSetBudget = async (adSetDbId, budget, expectedBudget) => {
        try {
            await updateAdSetBudgetMutation({
                variables: {
                    adSetId: adSetDbId, // Use database ID, not external ID
                    budget: budget * 100, // Convert dollars to cents
                    ...(expectedBudget != null ? { expectedBudget } : {}),
                },
            })
        } catch (error) {
            console.error('Error updating ad set budget:', error)
            const guardMessage = describeBudgetGuardError(error, formatAmount)
            const errorMessage = guardMessage || error.message || 'Failed to update ad set budget. Please try again.'
            showNotification(errorMessage, 'error', guardMessage ? 10000 : 5000, null, null, true, true)
            throw error
        }
    }

    // `openedBudget` is the saved campaign budget (cents) the page showed: a budget changed meanwhile is not overwritten
    const updateCampaignBudget = async (adCampaignId, budget, openedBudget) => {
        try {
            await updateAdCampaignMutation({
                variables: {
                    adCampaignId,
                    // Convert dollars to cents
                    data: campaignBudgetEditFields(Math.round(budget * 100), openedBudget),
                },
            })
            await performRefetch()
        } catch (error) {
            const errorMessage =
                getCampaignBudgetErrorMessage(error) ||
                error.message ||
                'Failed to update the campaign budget. Please try again.'
            showNotification(errorMessage, 'error', 5000, null, null, true, true)
            throw error
        }
    }

    const toggleCampaignStatus = async (campaignId, currentStatus) => {
        try {
            await toggleCampaignStatusMutation({
                variables: { campaignId, currentStatus },
            })
        } catch (error) {
            console.error('Error toggling campaign status:', error)
            const errorMessage = error.message || 'Failed to toggle campaign status. Please try again.'
            showNotification(errorMessage, 'error', 5000, null, null, true, true)
            throw error
        }
    }

    const toggleAdSetStatus = async (adSetDbId, currentStatus) => {
        try {
            await toggleAdSetStatusMutation({
                variables: { adSetId: adSetDbId, currentStatus }, // Use database ID, not external ID
            })
        } catch (error) {
            console.error('Error toggling ad set status:', error)
            const guardMessage = describeBudgetGuardError(error, formatAmount)
            const errorMessage = guardMessage || error.message || 'Failed to toggle ad set status. Please try again.'
            showNotification(errorMessage, 'error', guardMessage ? 10000 : 5000, null, null, true, true)
            throw error
        }
    }

    const toggleAdStatus = async (adId, currentStatus) => {
        try {
            await toggleAdStatusMutation({
                variables: { adId, currentStatus },
            })
        } catch (error) {
            console.error('Error toggling ad status:', error)
            const errorMessage = error.message || 'Failed to toggle ad status. Please try again.'
            showNotification(errorMessage, 'error', 5000, null, null, true, true)
            throw error
        }
    }

    const deleteCampaign = async (campaignId) => {
        try {
            await deleteCampaignMutation({
                variables: { campaignId },
            })
            showNotification('Campaign deleted successfully', 'success', 3000)
        } catch (error) {
            console.error('Error deleting campaign:', error)
            const errorMessage = error.message || 'Failed to delete campaign. Please try again.'
            showNotification(errorMessage, 'error', 5000, null, null, true, true)
            throw error
        }
    }

    const deleteAdSet = async (adSetDbId) => {
        try {
            await deleteAdSetMutation({
                variables: { adSetId: adSetDbId }, // Use database ID
            })
            showNotification('Ad set deleted successfully', 'success', 3000)
        } catch (error) {
            console.error('Error deleting ad set:', error)
            const errorMessage = error.message || 'Failed to delete ad set. Please try again.'
            showNotification(errorMessage, 'error', 5000, null, null, true, true)
            throw error
        }
    }

    const deleteAd = async (adDbId) => {
        try {
            await deleteAdMutation({
                variables: { adId: adDbId }, // Use database ID
            })
            showNotification('Ad deleted successfully', 'success', 3000)
        } catch (error) {
            console.error('Error deleting ad:', error)
            const errorMessage = error.message || 'Failed to delete ad. Please try again.'
            showNotification(errorMessage, 'error', 5000, null, null, true, true)
            throw error
        }
    }

    const updateAdCampaignOrder = async (updates) => {
        try {
            await updateAdCampaignOrderMutation({
                variables: { updates },
            })
        } catch (error) {
            console.error('Error updating campaign order:', error)
            const errorMessage = error.message || 'Failed to save the new order. Please try again.'
            showNotification(errorMessage, 'error', 5000, null, null, true, true)
            throw error
        }
    }

    // Wrapper function for refetch to avoid circular reference issues
    const refreshData = async () => {
        await performRefetch()
    }

    return {
        loading,
        error,
        refetch: refreshData,
        updateAdSetBudget,
        updateCampaignBudget,
        toggleCampaignStatus,
        toggleAdSetStatus,
        toggleAdStatus,
        deleteCampaign,
        deleteAdSet,
        deleteAd,
        updateAdCampaignOrder,
        creativesById,
        pageTitle,
        pageIcon,
    }
}
